import { useContext, useEffect, useState, useRef } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { FiEdit2, FiHeart, FiUser, FiCalendar, FiMapPin, FiArrowRight, FiMail, FiChevronLeft, FiChevronRight } from "react-icons/fi";
import { CgProfile } from "react-icons/cg";
import { BsStars, BsPeople, BsPersonCheck } from "react-icons/bs";
import axios from "axios";

import UserContext from "../Authcontext";
import { API_BASE_URL } from "../api_config";
import Header from "../Header/Header";
import Footer from "../Foote/Footer";

// Import Swiper components
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Pagination } from "swiper/modules";

// Import CSS
import "./modern-home.css";
import "./tab-interface.css";

const ModernHome = () => {
  const { user, fetchUserData, token } = useContext(UserContext);
  const [dailyRecommendations, setDailyRecommendations] = useState([]);
  const [partnerPreferences, setPartnerPreferences] = useState([]);
  const [totalMatches, setTotalMatches] = useState([]);
  const [greeting, setGreeting] = useState("");
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("available");
  const [imagePreview, setImagePreview] = useState(null);
  const fileInputRef = useRef(null);
  
  // User statistics
  const [userStats, setUserStats] = useState({
    matches: 0,
    interests: 0,
    visitors: 0
  });
  
  // Location data
  const [locationData, setLocationData] = useState({
    city: "",
    state: "",
    country: ""
  });
  
  // Pagination states
  const [availablePage, setAvailablePage] = useState(1);
  const [partnerPage, setPartnerPage] = useState(1);
  const [totalPage, setTotalPage] = useState(1);
  const profilesPerPage = 9;

  const navigate = useNavigate();
  const location = useLocation();

  // Set greeting based on time of day
  useEffect(() => {
    const currentTime = new Date();
    const hours = currentTime.getHours();

    if (hours < 12) {
      setGreeting("Good Morning");
    } else if (hours < 18) {
      setGreeting("Good Afternoon");
    } else {
      setGreeting("Good Evening");
    }
  }, []);

  // Fetch user data
  useEffect(() => {
    fetchUserData();
  }, [fetchUserData]);

  // Redirect if user is not verified
  useEffect(() => {
    if (user.verify !== 1) {
      navigate("/user-under-verification", { replace: true });
    }
  }, [user.verify, navigate]);

  // Fetch daily recommendations
  useEffect(() => {
    if (user.user_id) {
      setLoading(true);
      axios
        .get(
          `${API_BASE_URL}/api/daily-rec`,
          {
            params: {
              id: user.user_id,
              gender: user.gender,
              membership: user.pay_membership,
              min_age: user.pmin_age,
              max_age: user.pmax_age,
              maritalStatus: user.pmarital_status,
              language: user.pmother_tongue,
              edu: user.phighest_education,
              empType: user.pemployment_type,
              occu: user.poccupation,
              income: user.pannual_income,
              religion: user.preligion,
              caste: user.pcaste,
              kulam: user.pkulam,
              star: user.pstar,
              raasi: user.praasi,
              height: user.pheight,
              weight: user.pweight,
              bodyType: user.pbody_type,
              complexion: user.pcomplexion,
              physicalStatus: user.pphysical_status,
              verify: 1
            },
          },
          {
            withCredentials: true,
          }
        )
        .then((res) => {
          if (Array.isArray(res.data)) {
            setDailyRecommendations(res.data);
          }
          setLoading(false);
        })
        .catch((error) => {
          console.error("Error fetching daily recommendations:", error);
          setLoading(false);
        });
    }
  }, [user]);

  // Fetch partner preferences
  useEffect(() => {
    if (user.user_id) {
      axios
        .get(
          `${API_BASE_URL}/api/part-pref`,
          {
            params: {
              id: user.user_id,
              gender: user.gender,
              membership: user.pay_membership,
              age: user.age,
              maritalStatus: user.marital_status,
              language: user.mother_tongue,
              edu: user.education,
              empType: user.employment_type,
              occu: user.occupation,
              income: user.income,
              religion: user.religion,
              caste: user.caste,
              kulam: user.kulam,
              star: user.star,
              raasi: user.raasi,
              height: user.height,
              weight: user.weight,
              bodyType: user.body_type,
              complexion: user.complexion,
              physicalStatus: user.physical,
            },
          },
          {
            withCredentials: true,
          }
        )
        .then((res) => {
          if (Array.isArray(res.data)) {
            setPartnerPreferences(res.data);
          }
        })
        .catch((error) => {
          console.error("Error fetching partner preferences:", error);
        });
    }
  }, [user]);

  // Fetch total matches
  useEffect(() => {
    if (user.user_id) {
      axios
        .get(
          `${API_BASE_URL}/api/total-matches`,
          {
            params: {
              id: user.user_id,
              gender: user.gender,
              membership: user.pay_membership,
            },
          },
          {
            withCredentials: true,
          }
        )
        .then((res) => {
          if (Array.isArray(res.data)) {
            setTotalMatches(res.data);
          }
        })
        .catch((error) => {
          console.error("Error fetching total matches:", error);
        });
    }
  }, [user]);

  // Fetch user statistics
  useEffect(() => {
    if (user.user_id) {
      axios
        .get(
          `${API_BASE_URL}/api/user-statistics`,
          {
            params: {
              id: user.user_id
            },
          },
          {
            withCredentials: true,
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        )
        .then((res) => {
          if (res.data) {
            setUserStats({
              matches: res.data.matches_count || totalMatches.length || 0,
              interests: res.data.interests_count || 0,
              visitors: res.data.visitors_count || 0
            });
          }
        })
        .catch((error) => {
          console.error("Error fetching user statistics:", error);
          // Fallback to using available data
          setUserStats({
            matches: totalMatches.length || 0,
            interests: user.interests_count || 0,
            visitors: user.visitors_count || 0
          });
        });
    }
  }, [user.user_id, token, totalMatches.length, user.interests_count, user.visitors_count]);

  // Fetch location data if city is a number (ID)
  useEffect(() => {
    if (user.city && !isNaN(user.city)) {
      // City is a number/ID, so fetch the location data
      axios
        .get(
          `${API_BASE_URL}/api/location/${user.city}`,
          {
            withCredentials: true,
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        )
        .then((res) => {
          if (res.data) {
            setLocationData({
              city: res.data.city_name || "",
              state: res.data.state_name || "",
              country: res.data.country_name || ""
            });
          }
        })
        .catch((error) => {
          console.error("Error fetching location data:", error);
          // If there's an error, try to use any string data available
          if (typeof user.city === 'string' && user.city.trim() !== '') {
            setLocationData({
              city: user.city,
              state: user.state || "",
              country: user.country || ""
            });
          }
        });
    } else if (typeof user.city === 'string' && user.city.trim() !== '') {
      // City is already a string, use it directly
      setLocationData({
        city: user.city,
        state: user.state || "",
        country: user.country || ""
      });
    }
  }, [user.city, user.state, user.country, token]);

  // Handle tab change
  const handleTabChange = (tab) => {
    setActiveTab(tab);
  };

  // Pagination handlers
  const handlePageChange = (page, tabType) => {
    if (tabType === 'available') {
      setAvailablePage(page);
    } else if (tabType === 'partner') {
      setPartnerPage(page);
    } else if (tabType === 'total') {
      setTotalPage(page);
    }
  };

  // Get paginated profiles
  const getPaginatedProfiles = (profiles, page) => {
    const startIndex = (page - 1) * profilesPerPage;
    const endIndex = startIndex + profilesPerPage;
    return profiles.slice(startIndex, endIndex);
  };

  // Calculate total pages
  const getTotalPages = (totalItems) => {
    return Math.ceil(totalItems / profilesPerPage);
  };

  // Render pagination component
  const renderPagination = (currentPage, totalItems, tabType) => {
    const totalPages = getTotalPages(totalItems);
    
    if (totalPages <= 1) return null;
    
    const renderPageButtons = () => {
      const buttons = [];
      
      // Always show first page
      buttons.push(
        <div 
          key="page-1" 
          className={`modern-pagination-button ${currentPage === 1 ? 'active' : ''}`}
          onClick={() => handlePageChange(1, tabType)}
        >
          1
        </div>
      );
      
      // Show ellipsis if needed
      if (currentPage > 3) {
        buttons.push(
          <div key="ellipsis-1" className="modern-pagination-ellipsis">...</div>
        );
      }
      
      // Show current page and surrounding pages
      for (let i = Math.max(2, currentPage - 1); i <= Math.min(totalPages - 1, currentPage + 1); i++) {
        if (i === 1 || i === totalPages) continue; // Skip first and last page as they're always shown
        
        buttons.push(
          <div 
            key={`page-${i}`} 
            className={`modern-pagination-button ${currentPage === i ? 'active' : ''}`}
            onClick={() => handlePageChange(i, tabType)}
          >
            {i}
          </div>
        );
      }
      
      // Show ellipsis if needed
      if (currentPage < totalPages - 2) {
        buttons.push(
          <div key="ellipsis-2" className="modern-pagination-ellipsis">...</div>
        );
      }
      
      // Always show last page if there's more than one page
      if (totalPages > 1) {
        buttons.push(
          <div 
            key={`page-${totalPages}`} 
            className={`modern-pagination-button ${currentPage === totalPages ? 'active' : ''}`}
            onClick={() => handlePageChange(totalPages, tabType)}
          >
            {totalPages}
          </div>
        );
      }
      
      return buttons;
    };
    
    return (
      <div className="modern-pagination">
        <div 
          className={`modern-pagination-arrow ${currentPage === 1 ? 'disabled' : ''}`}
          onClick={() => currentPage > 1 && handlePageChange(currentPage - 1, tabType)}
        >
          <FiChevronLeft />
        </div>
        
        {renderPageButtons()}
        
        <div 
          className={`modern-pagination-arrow ${currentPage === totalPages ? 'disabled' : ''}`}
          onClick={() => currentPage < totalPages && handlePageChange(currentPage + 1, tabType)}
        >
          <FiChevronRight />
        </div>
      </div>
    );
  };

  // Handle image upload
  const handleImageChange = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onloadend = () => {
      setImagePreview(reader.result);
    };
    reader.readAsDataURL(file);

    // Upload the selected image to the backend
    const formData = new FormData();
    formData.append("image", file);

    // Make the API call to upload the image to the backend
    axios
      .put(`${API_BASE_URL}/upload/update-img`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        withCredentials: true,
      })
      .then((res) => {
        fetchUserData();
      })
      .catch((error) => {
        console.error("Error updating image:", error);
        alert("Error updating image. Please try again.");
      });
  };

  // Render profile card with overlay design
  const renderProfileCard = (profile) => (
    <Link 
      to="/view-profile" 
      state={{ userId: profile.user_id }}
      className="modern-profile-card"
    >
      <img
        src={profile.filename ? `${API_BASE_URL}/uploads/${profile.filename}` : "/default-avatar.png"}
        alt={profile.first_name}
        style={{height: "300px"}}
        className="modern-profile-image"
      />
      <div className="modern-profile-overlay">
        <div className="modern-profile-name">{profile.first_name} {profile.last_name}</div>
        <div className="modern-profile-info">
          <span>{profile.age} yrs</span> • 
          <span>{profile.height}</span> • 
          <span>{profile.city}</span>
        </div>
      </div>
    </Link>
  );


  // Empty state component
  const EmptyState = ({ title, message }) => (
    <div className="modern-empty-tab">
      <div className="modern-empty-tab-icon">
        <CgProfile />
      </div>
      <h3 className="modern-empty-tab-title">{title}</h3>
      <p className="modern-empty-tab-message">{message}</p>
    </div>
  );

  return (
    <>
      <Header file={user.filename} name={user.first_name} />
      <div className="modern-home-container">
        {/* Decorative elements */}
        <div className="home-decoration home-decoration-1"></div>
        <div className="home-decoration home-decoration-2"></div>
        
        <div className="modern-home-content">
          {/* Profile Sidebar */}
          <div className="modern-profile-sidebar">
            <div className="modern-profile-card">
              <div className="modern-profile-header">
                <div className="modern-profile-avatar-container">
                  <img
                    src={imagePreview || (user.filename ? `${API_BASE_URL}/uploads/${user.filename}` : "/default-avatar.png")}
                    alt={user.first_name}
                    className="modern-profile-avatar"
                  />
                  <label htmlFor="profile-image-upload" className="modern-profile-edit-button">
                    <FiEdit2 size={16} />
                  </label>
                  <input
                    type="file"
                    id="profile-image-upload"
                    ref={fileInputRef}
                    style={{ display: "none" }}
                    accept="image/*"
                    onChange={handleImageChange}
                  />
                </div>
                <h2 className="modern-profile-name">{user.first_name} {user.last_name}</h2>
                <p className="modern-profile-greeting">{greeting}!</p>
                
                <div className="modern-profile-stats">
                  <div className="modern-profile-stat">
                    <div className="modern-profile-stat-value">{userStats.matches}</div>
                    <div className="modern-profile-stat-label">Matches</div>
                  </div>
                  <div className="modern-profile-stat">
                    <div className="modern-profile-stat-value">{userStats.interests}</div>
                    <div className="modern-profile-stat-label">Interests</div>
                  </div>
                  <div className="modern-profile-stat">
                    <div className="modern-profile-stat-value">{userStats.visitors}</div>
                    <div className="modern-profile-stat-label">Visitors</div>
                  </div>
                </div>
              </div>
              
              <div className="modern-profile-details">
                <div className="modern-profile-detail-item">
                  <div className="modern-profile-detail-icon">
                    <FiCalendar />
                  </div>
                  <div className="modern-profile-detail-content">
                    <div className="modern-profile-detail-label">Age</div>
                    <div className="modern-profile-detail-value">{user.age || "Not set"} years</div>
                  </div>
                </div>
                
                <div className="modern-profile-detail-item">
                  <div className="modern-profile-detail-icon">
                    <BsPersonCheck />
                  </div>
                  <div className="modern-profile-detail-content">
                    <div className="modern-profile-detail-label">Profession</div>
                    <div className="modern-profile-detail-value">{user.occupation || "Not set"}</div>
                  </div>
                </div>
                
                <div className="modern-profile-detail-item">
                  <div className="modern-profile-detail-icon">
                    <FiMail />
                  </div>
                  <div className="modern-profile-detail-content">
                    <div className="modern-profile-detail-label">Email</div>
                    <div className="modern-profile-detail-value">{user.email || "Not set"}</div>
                  </div>
                </div>
              </div>
              
              <div className="modern-profile-actions">
                <div className="modern-profile-actions2">
                <Link 
                  to="/view-profile" 
                  state={{ userId: user.user_id }}
                  className="modern-profile-action-button modern-button-primary"
                >
                  <FiUser size={16} />
                  <span>View Profile</span>
                </Link>
                <Link 
                  to="/edit-profile" 
                  className="modern-profile-action-button modern-button-secondary"
                >
                  <FiEdit2 size={16} />
                  <span>Edit Profile</span>
                </Link>
                </div>
                
                <Link 
                  to="/edit-partner-preferences" 
                  className="modern-profile-action-button modern-button-preference"
                >
                  <FiHeart size={16} />
                  <span>Edit Partner Preference</span>
                </Link>
              </div>
            </div>
          </div>
          
          {/* Main Content with Tabs */}
          <div className="modern-main-content">
            {/* Main Tabs */}
            <div className="modern-main-tabs">
              <div 
                className={`modern-main-tab ${activeTab === 'available' ? 'active' : ''}`}
                onClick={() => handleTabChange('available')}
              >
                Available Profiles
              </div>
              <div 
                className={`modern-main-tab ${activeTab === 'partner' ? 'active' : ''}`}
                onClick={() => handleTabChange('partner')}
              >
                Partner Preference
              </div>
              <div 
                className={`modern-main-tab ${activeTab === 'total' ? 'active' : ''}`}
                onClick={() => handleTabChange('total')}
              >
                Total Matches
              </div>
            </div>
            
            {/* Available Profiles Tab Content */}
            <div className={`modern-tab-content ${activeTab === 'available' ? 'active' : ''}`}>
              <div className="modern-profile-grid-container">
                {dailyRecommendations.length > 0 ? (
                  <>
                    <div  className="modern-profile-grid" >
                      {getPaginatedProfiles(dailyRecommendations, availablePage).map((profile) => (
                        <div key={profile.user_id}  className="profile-card-wrapper">
                          {renderProfileCard(profile)}
                        </div>
                      ))}
                    </div>
                    {renderPagination(availablePage, dailyRecommendations.length, 'available')}
                  </>
                ) : (
                  <EmptyState 
                    title="No Available Profiles" 
                    message="We're working on finding profiles that match your criteria. Check back soon!" 
                  />
                )}
              </div>
            </div>
            
            {/* Partner Preference Tab Content */}
            <div className={`modern-tab-content ${activeTab === 'partner' ? 'active' : ''}`}>
              <div className="modern-profile-grid-container">
                {partnerPreferences.length > 0 ? (
                  <>
                    <div className="modern-profile-grid">
                      {getPaginatedProfiles(partnerPreferences, partnerPage).map((profile) => (
                        <div key={profile.user_id} className="profile-card-wrapper">
                          {renderProfileCard(profile)}
                        </div>
                      ))}
                    </div>
                    {renderPagination(partnerPage, partnerPreferences.length, 'partner')}
                  </>
                ) : (
                  <EmptyState 
                    title="No Partner Preference Matches" 
                    message="Update your partner preferences to see more relevant matches." 
                  />
                )}
              </div>
            </div>
            
            {/* Total Matches Tab Content */}
            <div className={`modern-tab-content ${activeTab === 'total' ? 'active' : ''}`}>
              <div className="modern-profile-grid-container">
                {totalMatches.length > 0 ? (
                  <>
                    <div className="modern-profile-grid">
                      {getPaginatedProfiles(totalMatches, totalPage).map((profile) => (
                        <div key={profile.user_id} className="profile-card-wrapper">
                          {renderProfileCard(profile)}
                        </div>
                      ))}
                    </div>
                    {renderPagination(totalPage, totalMatches.length, 'total')}
                  </>
                ) : (
                  <EmptyState 
                    title="No Matches Found" 
                    message="We're still searching for your perfect match. Check back soon!" 
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default ModernHome;
